import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import { AddRegular, DismissRegular, EditRegular, PersonAddRegular } from '@fluentui/react-icons';
import { Module } from '../../../../models/module';
import { User } from '../../../../models/users';
import { Organization } from '../../../../models/organizations';
import { getUsersByModuleAndRole, getUserByEmail } from '../../../../services/usersService';
import { getRolesByType, createUserRoleAssignment } from '../../../../services/roleService';
import { getOrganizations } from '../../../../services/organizationService';
import ErrorDialog from '../../ErrorDialog';
import UserDrawer from '../../../drawers/UserDrawer';

interface SystemManagersProps {
  tenderingOptions: Module[];
}

const ROLE_NAME = 'Ramavtalsansvarig';

const SystemManagers: React.FC<SystemManagersProps> = ({ tenderingOptions }) => {
  const [selectedTenderingID, setSelectedTenderingID] = useState<number>(tenderingOptions[0]?.moduleID);
  const [managers, setManagers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  // Add manager dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addFetchedUser, setAddFetchedUser] = useState<User | undefined>(undefined);
  const [addFetchError, setAddFetchError] = useState('');
  const [addIsFetching, setAddIsFetching] = useState(false);
  const [addIsSaving, setAddIsSaving] = useState(false);

  // Edit user drawer state
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);

  useEffect(() => {
    getOrganizations().then(setOrganizations).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedTenderingID) {
      fetchManagers(selectedTenderingID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenderingID]);

  const fetchManagers = async (moduleID: number) => {
    setLoading(true);
    setManagers([]);
    try {
      const data = await getUsersByModuleAndRole(moduleID, ROLE_NAME);
      setManagers(data);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const getOrganizationName = (organizationID: number): string => {
    return organizations.find(o => o.organizationID === organizationID)?.organizationName || '—';
  };

  const groupedManagers = managers.reduce<Record<number, User[]>>((acc, manager) => {
    const key = manager.organizationID ?? 0;
    if (!acc[key]) acc[key] = [];
    acc[key].push(manager);
    return acc;
  }, {});

  const handleOpenAddDialog = () => {
    setAddEmail('');
    setAddFetchedUser(undefined);
    setAddFetchError('');
    setAddDialogOpen(true);
  };

  const handleFetchUser = async () => {
    if (!addEmail) {
      setAddFetchError('Vänligen ange en e-postadress');
      return;
    }
    setAddIsFetching(true);
    setAddFetchError('');
    try {
      const user = await getUserByEmail(addEmail);
      const userOrg = organizations.find(o => o.organizationID === user.organizationID);
      if (!userOrg || userOrg.organizationTypeID !== 2) {
        setAddFetchError('Användaren tillhör inte en giltig organisation för ramavtalsansvarig');
        setAddFetchedUser(undefined);
        return;
      }
      setAddFetchedUser(user);
    } catch {
      setAddFetchError('Ingen användare hittades med denna e-postadress');
      setAddFetchedUser(undefined);
    } finally {
      setAddIsFetching(false);
    }
  };

  const handleAddManager = async () => {
    if (!addFetchedUser || !selectedTenderingID) return;
    setAddIsSaving(true);
    try {
      const roles = await getRolesByType('Procurer');
      const roleID = roles.find((r: any) => r.roleName === ROLE_NAME)?.roleID;
      await createUserRoleAssignment(addFetchedUser.userID, roleID, selectedTenderingID);
      await fetchManagers(selectedTenderingID);
      setAddDialogOpen(false);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    } finally {
      setAddIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap gap-4 items-end justify-between">
        <div className="flex-1 min-w-[200px]">
          <h5 className="text-sm font-semibold mb-2">Välj Ramavtal</h5>
          <Dropdown
            placeholder="Välj Ramavtal"
            value={tenderingOptions.find(t => t.moduleID === selectedTenderingID)?.moduleName || ''}
            style={{ width: '100%', minWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            positioning={{ autoSize: false }}
            listbox={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
            onOptionSelect={(_, data) => setSelectedTenderingID(Number(data.optionValue))}
          >
            {tenderingOptions.map((option) => (
              <Option key={option.moduleID} value={option.moduleID.toString()}>
                {option.moduleName}
              </Option>
            ))}
          </Dropdown>
        </div>
        <Button
          appearance="primary"
          icon={<PersonAddRegular />}
          onClick={handleOpenAddDialog}
          disabled={!selectedTenderingID}
        >
          Lägg till ny ramavtalsansvarig
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="large" label="Laddar..." />
        </div>
      ) : (
        <div className="space-y-4">
          {Object.keys(groupedManagers).length === 0 ? (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHeaderCell className="font-semibold">Namn</TableHeaderCell>
                    <TableHeaderCell className="font-semibold">E-post</TableHeaderCell>
                    <TableHeaderCell className="font-semibold">Telefon</TableHeaderCell>
                    <TableHeaderCell style={{ width: '60px' }} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-gray-500 py-6">
                      Inga ramavtalsansvariga hittades
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : (
            Object.entries(groupedManagers).map(([orgID, orgManagers]) => (
              <div key={orgID} className="overflow-hidden rounded-lg border border-gray-200">
                <div className="bg-gray-100 px-4 py-2">
                  <Text weight="semibold">{getOrganizationName(Number(orgID))}</Text>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHeaderCell className="font-semibold">Namn</TableHeaderCell>
                      <TableHeaderCell className="font-semibold">E-post</TableHeaderCell>
                      <TableHeaderCell className="font-semibold">Telefon</TableHeaderCell>
                      <TableHeaderCell style={{ width: '60px' }} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgManagers.map((manager, index) => (
                      <TableRow
                        key={manager.userID}
                        className={`group ${index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-50'}`}
                      >
                        <TableCell>{manager.firstName} {manager.lastName}</TableCell>
                        <TableCell>{manager.email}</TableCell>
                        <TableCell>{manager.phoneNumber || '—'}</TableCell>
                        <TableCell style={{ width: '60px' }}>
                          <Tooltip content="Redigera konto" relationship="label">
                            <Button
                              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              shape="circular"
                              appearance="subtle"
                              icon={<EditRegular />}
                              onClick={() => { setSelectedUser(manager); setUserDrawerOpen(true); }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add manager dialog */}
      <Dialog
        open={addDialogOpen}
        onOpenChange={(_, data) => !data.open && setAddDialogOpen(false)}
        modalType="alert"
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  shape="circular"
                  aria-label="Stäng"
                  icon={<DismissRegular />}
                  onClick={() => setAddDialogOpen(false)}
                />
              }
            >
              Lägg till ramavtalsansvarig
            </DialogTitle>
            <DialogContent>
              <Field
                label="E-post:"
                validationMessage={addFetchError}
                validationState={addFetchError ? 'error' : undefined}
              >
                <Input
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  disabled={!!addFetchedUser}
                />
              </Field>
              {!addFetchedUser && (
                <Button
                  appearance="primary"
                  disabled={addIsFetching}
                  onClick={handleFetchUser}
                  style={{ marginTop: '8px' }}
                >
                  {addIsFetching ? 'Hämtar användare...' : 'Hämta användare'}
                </Button>
              )}
              {addFetchedUser && (
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <Text weight="semibold" block style={{ marginBottom: '8px' }}>Användarinformation:</Text>
                  <Text block style={{ marginBottom: '4px' }}><strong>Förnamn:</strong> {addFetchedUser.firstName || 'Ej angivet'}</Text>
                  <Text block style={{ marginBottom: '4px' }}><strong>Efternamn:</strong> {addFetchedUser.lastName || 'Ej angivet'}</Text>
                  <Text block style={{ marginBottom: '4px' }}><strong>Telefon:</strong> {addFetchedUser.phoneNumber || 'Ej angivet'}</Text>
                  <Button onClick={() => { setAddFetchedUser(undefined); setAddEmail(''); }} style={{ marginTop: '12px' }}>
                    Hämta annan användare
                  </Button>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddDialogOpen(false)}>Avbryt</Button>
              <Button
                appearance="primary"
                disabled={!addFetchedUser || addIsSaving}
                onClick={handleAddManager}
              >
                {addIsSaving ? 'Sparar...' : 'Spara'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {selectedUser && (
        <UserDrawer
          open={userDrawerOpen}
          onClose={() => { setUserDrawerOpen(false); setSelectedUser(undefined); }}
          user={selectedUser}
          organizations={organizations}
          onUserUpdated={(updated) => {
            setManagers(prev => prev.map(m => m.userID === updated.userID ? { ...m, ...updated } : m));
          }}
        />
      )}

      {errorDialogOpen && (
        <ErrorDialog isOpen={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} />
      )}
    </div>
  );
};

export default SystemManagers;
